import React from 'react';
import { ClerkProvider } from '@clerk/clerk-react';
import { BrowserRouter as Router } from 'react-router-dom';
import AppRoutes from './AppRoutes';
import './index.css';
import { AuthProvider } from '@/features-flat/auth/useAuthContext';
import { ErrorProvider } from '@/shared/contexts/ErrorContext';
import { FeedbackProvider } from '@/shared/contexts/FeedbackContext';
import { Toaster } from '@/components/ui/toaster';
import AuthRedirectHandler from '@/components/auth/AuthRedirectHandler';

// Get Clerk publishable key from environment variable
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const FRONTEND_API = import.meta.env.VITE_CLERK_FRONTEND_API_URL; // Enable frontend API URL for domain fix

// Enhanced debugging
console.log("Clerk Key Debug:", {
  keyExists: !!PUBLISHABLE_KEY,
  keyLength: PUBLISHABLE_KEY?.length,
  keyStart: PUBLISHABLE_KEY?.substring(0, 7),
  hasWhitespace: PUBLISHABLE_KEY?.match(/\s/),
  envVars: import.meta.env
});

console.log("Clerk Frontend API URL:", FRONTEND_API);

// Error component for missing environment variables
const ConfigurationError: React.FC = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
    <div className="max-w-lg w-full bg-white rounded-lg shadow-lg p-6 text-center">
      <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.316 15.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      <h1 className="text-xl font-semibold text-gray-900 mb-2">Missing Secrets Configuration</h1>
      <p className="text-gray-600 mb-4">
        Missing Clerk authentication key. Please configure your secrets properly.
      </p>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left mb-4">
        <div className="flex items-start space-x-2">
          <div className="text-2xl">🔒</div>
          <div>
            <h3 className="text-sm font-medium text-blue-900 mb-1">Replit Development</h3>
            <p className="text-sm text-blue-700 mb-2">
              Click the <strong>Secrets</strong> tab (🔒 icon) in the left sidebar and add:
            </p>
            <code className="text-xs text-blue-800 bg-blue-100 px-2 py-1 rounded block">
              VITE_CLERK_PUBLISHABLE_KEY = pk_test_your_key_here
            </code>
          </div>
        </div>
      </div>
      <div className="bg-gray-50 rounded-lg p-4 text-left mb-4">
        <div className="flex items-start space-x-2">
          <div className="text-2xl">☁️</div>
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-1">Cloudflare Production</h3>
            <p className="text-sm text-gray-600 mb-2">
              Set environment variables in Cloudflare Pages dashboard
            </p>
            <code className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded block">
              wrangler pages secret put VITE_CLERK_PUBLISHABLE_KEY
            </code>
          </div>
        </div>
      </div>
      <button 
        onClick={() => window.location.reload()} 
        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        Retry After Setting Secrets
      </button>
      <p className="text-xs text-gray-500 mt-3">
        🚫 No .env files needed! Use platform-native secrets management.
      </p>
    </div>
  </div>
);

const App: React.FC = () => {
  // If missing environment variables, show error page
  if (!PUBLISHABLE_KEY) {
    console.error('Missing VITE_CLERK_PUBLISHABLE_KEY environment variable.');
    console.error('🔒 In Replit: Use the Secrets tab to add VITE_CLERK_PUBLISHABLE_KEY');
    console.error('☁️ In Production: Use Cloudflare Pages environment variables');
    console.error('❌ DO NOT use .env files - use proper secrets management!');
    
    return <ConfigurationError />;
  }

  return (
    <ClerkProvider 
      publishableKey={PUBLISHABLE_KEY}
      afterSignInUrl="https://portal.recurringvaluepodcast.com"
      afterSignUpUrl="https://portal.recurringvaluepodcast.com"
    >
      <FeedbackProvider>
        <ErrorProvider>
          <AuthProvider>
            <Router>
              <AuthRedirectHandler>
                <AppRoutes />
              </AuthRedirectHandler>
            </Router>
          </AuthProvider>
        </ErrorProvider>
        <Toaster />
      </FeedbackProvider>
    </ClerkProvider>
  );
};

export default App;
// Updated to use modern secrets management - NO .env files!
